<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { isFuture } from 'date-fns';
import i18n from '@/utils/i18n';
import { default as ButtonEl } from '@/components/shared/Button.vue';
import { capitalize } from 'lodash';
import CoverView from '@/views/shared/CoverView.vue';
import { formatRepeat } from '@/utils/helpers';
import ParticipantsBlock from '@/components/shared/blocks/ParticipantsBlock.vue';
import DepartmentsBlock from '@/components/shared/blocks/DepartmentsBlock.vue';
import TTS from '@/components/shared/Tts.vue';
import DateTimeBlock from '@/components/shared/blocks/DateTimeBlock.vue';
import TitleBlock from '@/components/shared/blocks/TitleBlock.vue';
import { useMeetingsStore } from '@/stores/meetings';
import AgendaBlock from '@/components/shared/blocks/AgendaBlock.vue';
import SuggestionBlock from '@/components/shared/blocks/SuggestionBlock.vue';
import LocationBlock from '@/components/shared/blocks/LocationBlock.vue';
import CategoryBlock from '@/components/shared/blocks/CategoryBlock.vue';
import BasicBlock from '@/components/shared/blocks/BasicBlock.vue';
import SuggestionsOverlay from '@/views/meetings/SuggestionsOverlay.vue';
import { IProfile } from '@/interfaces';
import AuthFlow from '@/components/shared/AuthFlow.vue';
import BlocksWrapper from '@/components/shared/blocks/BlocksWrapper.vue';

const meetingsStore = useMeetingsStore();
const route = useRoute();
const participating = ref<boolean>(true);
const showSuggestionsOverlay = ref<boolean>(false);
const showAuthFlow = ref<boolean>(false);

const meeting = computed(() => {
  return meetingsStore.selected;
});

const moduleColor = computed(() => {
  return route?.meta?.color;
});

const signupPossible = computed(() => {
  return (
    meetingsStore?.selected?.signupPossible &&
    !meetingsStore?.selected?.cancelled
  );
});

const optoutPossible = computed(() => {
  if (!meetingsStore?.selected?.startDate) return false;
  return isFuture(new Date(meetingsStore?.selected?.startDate));
});

const category = computed(() => {
  return capitalize(meetingsStore?.selected?.category);
});

const repeatText = computed(() => {
  const frequency = formatRepeat(
    meetingsStore?.selected?.repeat ?? { rrule: '' }
  );
  return frequency ? i18n.global.t(`rrule.${frequency}`) : '';
});

const save = async (profile: IProfile, pin: string) => {
  if (!meetingsStore.selected) return;

  const signupData = {
    subscription: {
      id: meetingsStore.selected.id,
    },
    profile: {
      ...profile,
      profileId: profile.id,
      profileType: profile.type,
      pin,
    },
    participating: participating.value,
  };

  await meetingsStore.meetingSignupScreen(signupData);
};

onMounted(() => {
  const meetingId = Array.isArray(route.params.id)
    ? route.params.id[0]
    : route.params.id;
  meetingsStore.getSelectedMeeting(meetingId);
});

async function setParticipating(isParticipating: boolean) {
  showAuthFlow.value = true;
  participating.value = isParticipating;
}

const pinOverlayTitle = computed(() => {
  return participating.value
    ? i18n.global.t('meetings.signUp')
    : i18n.global.t('meetings.optOut');
});

const pinOverlayTitleBold = computed(() => {
  return meetingsStore?.selected?.title;
});

const successOverlayTitle = computed(() => {
  return participating.value
    ? i18n.global.t('meetings.signedUp')
    : i18n.global.t('meetings.optedOut');
});
</script>

<template>
  <div
    class="meeting"
    v-if="meetingsStore.selected && meetingsStore.selected.id"
  >
    <CoverView
      :image-url="meetingsStore.selected.coverImage.source"
      :module-color="moduleColor"
    >
      <div class="meetingContent tts-content">
        <div>
          <div class="flex items-start justify-between">
            <TitleBlock :text="meetingsStore.selected.title" />
            <TTS />
          </div>

          <BlocksWrapper>
            <CategoryBlock v-if="category" :category="category" />

            <DateTimeBlock
              :start="meetingsStore.selected.startDate"
              :end="meetingsStore.selected.endDate"
            />

            <BasicBlock
              v-if="meetingsStore.selected.repeat && repeatText.length"
              :icon="['fal', 'repeat']"
              :ariaDescription="$t('global.repeats')"
              :text="repeatText"
            />

            <LocationBlock
              v-if="meetingsStore.selected.location"
              :location="meetingsStore.selected.location"
            />

            <AgendaBlock
              v-if="meetingsStore.selected.agendaPoints.length"
              :meeting="meetingsStore.selected"
            />

            <SuggestionBlock
              :meeting="meetingsStore.selected"
              :suggestions="meetingsStore.selected.suggestions || []"
            />

            <ParticipantsBlock
              v-if="meetingsStore.selected.participants.length"
              participant-type="participant"
              :can-trigger-overlay="true"
              :profiles="meetingsStore.selected.participants"
            />

            <DepartmentsBlock
              v-if="meetingsStore.selected.departments.length"
              :departments="meetingsStore.selected.departments"
              :borderBottom="false"
            />
          </BlocksWrapper>
        </div>
      </div>

      <div class="meetingActions w-max left-full sticky">
        <ButtonEl
          v-if="signupPossible"
          text="global.signUp"
          :icon="['far', 'circle-plus']"
          background="bg-green-400"
          textColor="text-neutral-0"
          class="shadow-xsm w-[205px] mb-4"
          rounded="full"
          @click="setParticipating(true)"
        />

        <ButtonEl
          v-if="optoutPossible"
          text="global.optOut"
          :icon="['far', 'minus-circle']"
          background="bg-red-400"
          textColor="text-neutral-0"
          class="shadow-xsm w-[205px]"
          rounded="full"
          @click="setParticipating(false)"
        />
      </div>
    </CoverView>

    <SuggestionsOverlay
      v-if="meeting && showSuggestionsOverlay"
      :suggestions="meeting.suggestions || []"
      :meeting="meeting"
      @close="showSuggestionsOverlay = false"
    />

    <AuthFlow
      v-if="showAuthFlow && meetingsStore.selected"
      :title="pinOverlayTitle"
      :title-bold="pinOverlayTitleBold"
      :title-success="successOverlayTitle"
      :participating="participating"
      :participants="meetingsStore.selected.participants"
      :shared-departments="meetingsStore.selected.departments"
      @close="showAuthFlow = false"
      @success="(profile, pin) => save(profile, pin)"
    />
  </div>
</template>

<style lang="scss">
.meeting {
  .meetingActions {
    bottom: 70px;
    padding-right: 80px;
  }
}
</style>
